(function () {
  const STICKY_SELECTOR = '.landing-page--sticky-add-to-cart';
  const CHAT_SELECTOR = '.shop-ai-chat-container';
  const ACTIVE_BODY_CLASS = 'sticky-add-to-cart-is-visible';
  const VIEWPORT_EVENTS = ['resize', 'orientationchange', 'scroll'];

  let stickyObserver;
  let stickyResizeObserver;
  let stickyVisibilityObserver;
  let observedSticky;
  let stickyIsIntersecting = false;
  let bodyObserver;
  let rafId;

  const scheduleUpdate = () => {
    if (rafId) return;
    rafId = window.requestAnimationFrame(() => {
      rafId = null;
      updateChatPosition();
    });
  };

  const elementIsVisible = (element) => {
    if (!element) return false;

    const styles = window.getComputedStyle(element);

    if (styles.display === 'none') return false;
    if (styles.visibility === 'hidden') return false;
    if (parseFloat(styles.opacity) === 0) return false;

    return element.offsetHeight > 0 && element.offsetWidth > 0;
  };

  const detachStickyObservers = () => {
    if (stickyObserver) {
      stickyObserver.disconnect();
      stickyObserver = undefined;
    }

    if (stickyResizeObserver) {
      stickyResizeObserver.disconnect();
      stickyResizeObserver = undefined;
    }

    if (stickyVisibilityObserver && observedSticky) {
      stickyVisibilityObserver.unobserve(observedSticky);
    }

    observedSticky = undefined;
    stickyIsIntersecting = false;
  };

  const ensureStickyVisibilityObserver = () => {
    if (!('IntersectionObserver' in window)) return undefined;

    if (!stickyVisibilityObserver) {
      stickyVisibilityObserver = new IntersectionObserver((entries) => {
        let shouldSchedule = false;

        entries.forEach((entry) => {
          if (entry.target === observedSticky) {
            stickyIsIntersecting = entry.isIntersecting || entry.intersectionRatio > 0;
            shouldSchedule = true;
          }
        });

        if (shouldSchedule) {
          scheduleUpdate();
        }
      }, { threshold: [0, 0.01] });
    }

    return stickyVisibilityObserver;
  };

  const attachStickyObservers = (sticky) => {
    if (!sticky) return;

    if (observedSticky && observedSticky !== sticky) {
      detachStickyObservers();
    }

    if (!stickyObserver) {
      stickyObserver = new MutationObserver(scheduleUpdate);
    }

    if (stickyObserver && observedSticky !== sticky) {
      stickyObserver.observe(sticky, { attributes: true, attributeFilter: ['style', 'class'] });
    }

    if ('ResizeObserver' in window && !stickyResizeObserver) {
      stickyResizeObserver = new ResizeObserver(scheduleUpdate);
    }

    if (stickyResizeObserver && observedSticky !== sticky) {
      stickyResizeObserver.observe(sticky);
    }

    const visibilityObserver = ensureStickyVisibilityObserver();

    if (visibilityObserver && observedSticky !== sticky) {
      if (observedSticky) {
        visibilityObserver.unobserve(observedSticky);
      }

      observedSticky = sticky;
      visibilityObserver.observe(sticky);
    }

    if (!visibilityObserver && observedSticky !== sticky) {
      observedSticky = sticky;
    }
  };

  const updateChatPosition = () => {
    const sticky = document.querySelector(STICKY_SELECTOR);
    const chat = document.querySelector(CHAT_SELECTOR);

    if (!sticky || !chat) {
      document.body.classList.remove(ACTIVE_BODY_CLASS);
      document.body.style.removeProperty('--sticky-atc-height');
      if (!sticky) {
        detachStickyObservers();
      }
      return;
    }

    attachStickyObservers(sticky);

    const stickyRect = sticky.getBoundingClientRect();
    const rectIsVisible = stickyRect.bottom > 0 && stickyRect.top < window.innerHeight;
    const stylesVisible = elementIsVisible(sticky);
    const isVisible = stylesVisible && (stickyIsIntersecting || rectIsVisible);

    if (isVisible) {
      const stickyHeight = Math.ceil(stickyRect.height);
      document.body.style.setProperty('--sticky-atc-height', `${stickyHeight}px`);
      document.body.classList.add(ACTIVE_BODY_CLASS);
    } else {
      document.body.classList.remove(ACTIVE_BODY_CLASS);
      document.body.style.removeProperty('--sticky-atc-height');
    }
  };

  const observeBody = () => {
    if (bodyObserver || !document.body) return;

    bodyObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;

      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const nodes = [...mutation.addedNodes, ...mutation.removedNodes];
          if (
            nodes.some((node) => {
              if (node.nodeType !== Node.ELEMENT_NODE) return false;
              const element = node;
              if (element.matches?.(CHAT_SELECTOR)) return true;
              if (element.matches?.(STICKY_SELECTOR)) return true;
              if (element.querySelector?.(CHAT_SELECTOR)) return true;
              return Boolean(element.querySelector?.(STICKY_SELECTOR));
            })
          ) {
            shouldUpdate = true;
            break;
          }
        } else if (mutation.type === 'attributes') {
          const target = mutation.target;
          if (target.nodeType === Node.ELEMENT_NODE) {
            const element = target;
            if (element.matches?.(CHAT_SELECTOR) || element.matches?.(STICKY_SELECTOR)) {
              shouldUpdate = true;
              break;
            }
          }
        }
      }

      if (shouldUpdate) {
        scheduleUpdate();
      }
    });

    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
  };

  const init = () => {
    observeBody();
    scheduleUpdate();

    VIEWPORT_EVENTS.forEach((eventName) => {
      const options = eventName === 'scroll' ? { passive: true } : undefined;
      window.addEventListener(eventName, scheduleUpdate, options);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
